import {AsyncPipe, DatePipe} from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {
  EAccountStatus,
  EAccountStatusInfo,
  ECustomerAccountStatus,
  EOrganizationActions,
} from '@app/accounts/enums/account-status.enum';
import {IOrganizationPayload} from '@app/accounts/models/accounts.model';
import {ICustomerRequest} from '@app/accounts/models/customer-request.model';
import {IListOfCustomer} from '@app/accounts/models/customer-response.model';
import {ICustomersState} from '@app/accounts/models/customer-state.model';
import {IRole} from '@app/accounts/models/role.model';
import {
  customersAction,
  loadRoles,
  resetUpdateOrganizationStatus,
  updateOrganization,
} from '@app/accounts/states/accounts.actions';
import {selectCustomers, selectRoles, selectUpdateOrganizationStatus} from '@app/accounts/states/accounts.selector';
import {DateFormat, OptionAll} from '@app/shared/constants';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {ECustomerOrganizationStatus} from '@app/shared/enums/header.enum';
import {ESortOrder} from '@app/shared/models/sort-order.enum';
import {selectCustomerOrganizationId, selectCustomerOrganizationStatus} from '@app/shared/state/header/header.selector';
import {Store} from '@ngrx/store';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {MenuItem, MenuItemCommandEvent, PrimeTemplate, SelectItem} from 'primeng/api';
import {Bind} from 'primeng/bind';
import {Button} from 'primeng/button';
import {InputText} from 'primeng/inputtext';
import {Message} from 'primeng/message';
import {Select} from 'primeng/select';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {TieredMenu} from 'primeng/tieredmenu';
import {Tooltip} from 'primeng/tooltip';
import {Observable, Subject, map, takeUntil, tap} from 'rxjs';

@Component({
  selector: 'app-account-list',
  templateUrl: './account-list.component.html',
  styleUrl: './account-list.component.scss',
  imports: [
    Bind,
    Button,
    FormsModule,
    InputText,
    Select,
    Tooltip,
    TableModule,
    PrimeTemplate,
    Tag,
    TieredMenu,
    Message,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class AccountListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);

  private readonly unsubscribe$ = new Subject<void>();
  customers: IListOfCustomer[] = [];
  customersState$: Observable<ICustomersState>;
  accountStatus$!: Observable<ECustomerOrganizationStatus>;
  accountStatusActive = ECustomerOrganizationStatus.Active;
  accountStatus!: ECustomerOrganizationStatus;
  firstRowIndex = 0;
  pageNumber = 1;
  pageSize = 5;
  pageSizeOptions: number[] = [5, 10, 15];
  totalRecords = 100;
  sortField = 'created';
  sortOrder = ESortOrder.DESC;
  eSortOrder = ESortOrder;
  actionStatus = EActionStatus;
  filtersApplied = false;
  filterByEmail?: string;
  filterByName?: string;
  filterByRole?: number | null;
  filterByLinkStatus?: number | null;
  dateFormat = DateFormat;
  roleFilterOptions: SelectItem[] = [];
  linkStatusFilterOptions: SelectItem[] = [
    OptionAll,
    {value: EAccountStatus.New, label: EAccountStatusInfo.New},
    {value: EAccountStatus.Active, label: EAccountStatusInfo.Active},
    {
      value: EAccountStatus.InActive,
      label: EAccountStatusInfo.InActive,
    },
  ];

  roles$: Observable<IRole[]>;
  currentRequest: ICustomerRequest | null = null;
  searchInProgress = false;
  clearInProgress = false;
  organizationId: number = 0;
  selectedCustomer: IListOfCustomer | null = null;

  constructor() {
    this.filterByLinkStatus = null;
    this.filterByRole = null;

    this.customersState$ = this.store.select(selectCustomers);

    this.customersState$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((customersResponse: ICustomersState) => {
          this.customers = this.prepareCustomers(customersResponse.successResponse?.listOfCustomers ?? []);
          this.totalRecords = customersResponse.successResponse?.totalNumberOfResults ?? 0;

          if (
            customersResponse.status !== EActionStatus.Initial &&
            customersResponse.status !== EActionStatus.Pending
          ) {
            this.searchInProgress = false;
            this.clearInProgress = false;
          }
        })
      )
      .subscribe();

    this.accountStatusSubscription();

    this.roles$ = this.store.select(selectRoles);
    this.roles$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((roles: IRole[]) => {
          this.roleFilterOptions = roles.map(status => {
            return {label: status.name, value: status.id} as SelectItem;
          });
          this.roleFilterOptions.unshift(OptionAll);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.store.dispatch(loadRoles());
    this.updateOrganizationStatusSubscription();

    this.store
      .select(selectCustomerOrganizationId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(customerOrganizationId => {
          if (customerOrganizationId) {
            this.organizationId = customerOrganizationId;
            if (this.accountStatus === ECustomerOrganizationStatus.Active) {
              this.store.dispatch(customersAction({request: this.getCustomersPayload(false)}));
            }
          }
        })
      )
      .subscribe();
  }

  accountStatusSubscription() {
    this.accountStatus$ = this.store.select(selectCustomerOrganizationStatus).pipe(
      takeUntil(this.unsubscribe$),
      tap(status => {
        this.accountStatus = status;
      })
    );
  }

  private updateOrganizationStatusSubscription(): void {
    this.store
      .select(selectUpdateOrganizationStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        map(updateOrganizationStatus => {
          if (updateOrganizationStatus) {
            this.onClear();
            this.store.dispatch(resetUpdateOrganizationStatus());
          }
        })
      )
      .subscribe();
  }

  onSearch() {
    this.searchInProgress = true;
    this.searchCustomers(true);
  }

  onClear() {
    this.clearInProgress = true;
    this.filterByEmail = '';
    this.filterByName = '';
    this.filterByLinkStatus = null;
    this.filterByRole = null;
    this.currentRequest = null;
    this.searchCustomers();
  }

  loadCustomers(event: TableLazyLoadEvent, isSearch = false) {
    this.firstRowIndex = isSearch || this.pageSize != event.rows ? 0 : (event.first ?? 0);
    this.pageSize = event.rows ?? 5;
    this.pageNumber = this.firstRowIndex / this.pageSize + 1;
    const payload = this.getCustomersPayload(isSearch);

    if (payload.organizationId) {
      const request = {...this.currentRequest, ...payload};
      if (isSearch) {
        this.currentRequest = request;
      }
      this.store.dispatch(customersAction({request: request}));
    }
  }

  getCustomerStatusName(statusId: number): string {
    let status = '';
    switch (statusId) {
      case EAccountStatus.New:
        status = EAccountStatusInfo.New;
        break;
      case EAccountStatus.Active:
        status = EAccountStatusInfo.Active;
        break;
      case EAccountStatus.InActive:
        status = EAccountStatusInfo.InActive;
        break;
    }
    return status;
  }

  getCustomerStatusStyle(statusId: number): string {
    let classTag = '';
    switch (statusId) {
      case EAccountStatus.New:
        classTag = 'p-tag-new';
        break;
      case EAccountStatus.Active:
        classTag = 'p-tag-info';
        break;
      case EAccountStatus.InActive:
        classTag = 'p-tag-inactive';
        break;
    }
    return classTag;
  }

  private prepareCustomers(customers: IListOfCustomer[]): IListOfCustomer[] {
    return (
      customers.map(customer => ({
        ...customer,
        customerMenuItems: this.getMenuItems(customer),
      })) ?? []
    );
  }

  private getMenuItems(customer: IListOfCustomer): MenuItem[] {
    const edit = {
      label: this.translate.instant('Accounts.Edit'),
      icon: 'pi pi-pencil',
      command: (menuItem: MenuItemCommandEvent) => this.editAccount(menuItem),
      state: {
        customerId: customer.customerId,
        organizationId: this.organizationId,
        statusId: EOrganizationActions.Deactivate,
      },
    };
    const activate = {
      label: this.translate.instant('Accounts.Activate'),
      icon: 'pi pi-check',
      command: (menuItem: MenuItemCommandEvent) => {
        this.updateOrganization(menuItem);
      },
      state: {
        customerId: customer.customerId,
        organizationId: this.organizationId,
        statusId: EOrganizationActions.Activate,
      },
    };
    const deactivate = {
      label: this.translate.instant('Accounts.Deactivate'),
      icon: 'pi pi-times',
      command: (menuItem: MenuItemCommandEvent) => this.updateOrganization(menuItem),
      state: {
        customerId: customer.customerId,
        organizationId: this.organizationId,
        statusId: EOrganizationActions.Deactivate,
      },
    };
    const menu = [edit] as MenuItem[];
    if (customer.customerOrganizationStatusId === EAccountStatus.New) {
      if (customer.customerStatusId === ECustomerAccountStatus.Verified) {
        menu.push(activate);
        menu.push(deactivate);
      }
    } else if (customer.customerOrganizationStatusId === EAccountStatus.Active) {
      menu.push(deactivate);
    } else if (customer.customerOrganizationStatusId === EAccountStatus.InActive) {
      menu.push(activate);
    }
    return menu;
  }

  editAccount(event: MenuItemCommandEvent): void {
    this.router.navigate([`/accounts/edit-account`], {
      queryParams: {id: event.item?.state?.['customerId']},
    });
  }

  updateOrganization(instituteInfo: MenuItemCommandEvent): void {
    this.store.dispatch(
      updateOrganization({
        payload: this.getOrganizationPayload(instituteInfo),
      })
    );
  }

  private getOrganizationPayload(event: MenuItemCommandEvent): IOrganizationPayload {
    return {
      customerId: event.item?.state?.['customerId'],
      organizationId: event.item?.state?.['organizationId'],
      statusId: event.item?.state?.['statusId'],
    } as IOrganizationPayload;
  }

  private getCustomersPayload(isSearch: boolean): ICustomerRequest {
    const payload: ICustomerRequest = {
      organizationId: this.organizationId,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
    };

    if (isSearch) {
      if (this.filterByName) {
        payload.name = this.filterByName;
      }
      if (this.filterByEmail) {
        payload.email = this.filterByEmail;
      }
      if (this.filterByRole) {
        payload.roleId = this.filterByRole;
      }
      if (this.filterByLinkStatus) {
        payload.linkStatus = this.filterByLinkStatus;
      }
    }

    return payload;
  }

  private searchCustomers(isSearch = false): void {
    this.firstRowIndex = 0;
    const event = {
      first: this.firstRowIndex,
      rows: this.pageSize,
    } as TableLazyLoadEvent;

    this.loadCustomers(event, isSearch);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  addAccount(): void {
    this.router.navigate(['./accounts/add-account']);
  }
}
